import React, { useState } from 'react';
import { MdArrowBack, MdCheckCircle, MdRadioButtonUnchecked, MdFeedback, MdRocketLaunch } from 'react-icons/md';
import { submitTesterFeedback } from '../services/backendAPI';

const FEATURES = [
  {
    number: 1,
    title: 'Fix the data that goes into your appointment',
    status: 'queued',
    problem: 'When a customer reaches the "Review your appointment" screen before confirming, the contact details shown (name, email, phone, postal code) are fake placeholder values — not the actual logged-in customer\'s information. The customer\'s savings range and follow-up answers are also silently discarded before they reach the advisor-matching step.',
    goal: 'Every piece of information the customer enters during the booking flow actually flows through and appears where it\'s supposed to: on the review screen, in the advisor match, and in the final confirmation.',
    outcome: 'Customers see their own name and contact details on the review screen. The advisor recommended to them is correctly weighted by their savings bracket and specific goal. Nothing a customer answers gets silently ignored.',
  },
  {
    number: 2,
    title: 'Save what we learn about the customer',
    status: 'queued',
    problem: 'When a customer completes the Swipe flow, their preferences — communication style, preferred meeting format, language — are saved only on that specific device in that browser. If they log in from a different device, or come back a week later, everything is blank again. The appointment they just booked is also never stored anywhere.',
    goal: 'After a customer completes the booking flow, their preferences and the details of their appointment are saved to their account on the server — the same place the app already stores everything else it knows about that customer.',
    outcome: 'Returning customers skip questions they\'ve already answered. The booking shows up in the customer\'s history. Data is available across any device they use.',
  },
  {
    number: 3,
    title: 'Help customers who don\'t need an advisor',
    status: 'queued',
    problem: 'The first screen of the Swipe flow asks: "Could you handle this yourself in 2 minutes?" and offers a button labelled "Yes — take me there." Right now that button does nothing useful — it just sends the customer back to the beginning of the Swipe flow.',
    goal: 'When a customer says they can self-serve, actually help them do that. Based on what they typed about their situation, route them to the right place in the app (rates screen, accounts, transfers, etc.).',
    outcome: 'Customers who can handle things themselves get there faster without being forced into a booking flow they don\'t need. Better experience, fewer unnecessary advisor bookings.',
  },
  {
    number: 4,
    title: 'Real advisor photos and a larger roster',
    status: 'queued',
    problem: 'Three of the six advisors in the matching results (Priya, David, and Sarah) show as a grey silhouette placeholder instead of a real photo. Six advisors is also a thin roster — some customer situations don\'t match well against such a small pool.',
    goal: 'All advisors have a real portrait photo. The roster grows from 6 to 9, with the new advisors covering specialties and backgrounds that round out the existing team (Tax Planning, Newcomer to Canada, Youth & Student).',
    outcome: 'The advisor cards look polished and complete. Customers with more niche financial situations get a closer match. The matching algorithm has more candidates to score.',
  },
  {
    number: 5,
    title: 'Notify the advisor when a booking is confirmed',
    status: 'queued',
    problem: 'When a customer confirms an appointment, the advisor they\'ve been matched with receives no signal that anything has happened. In a real system this would trigger a calendar invite or internal notification. Right now it\'s a dead end.',
    goal: 'The moment a customer confirms their booking, the system fires a notification to the advisor — writing the appointment details to a structured log, and showing the customer a message confirming their advisor has been notified.',
    outcome: 'The booking flow has a real conclusion. Advisors (or anyone reviewing the demo) can see incoming appointment requests in the notification log. The customer receives clear confirmation that their advisor knows they\'re coming.',
  },
  {
    number: 6,
    title: 'A downloadable booking confirmation',
    status: 'queued',
    problem: 'After confirming an appointment, the customer sees a confirmation screen — but there\'s no way to take that confirmation with them. No download, no file, nothing to forward to a colleague or keep for reference. The reference number shown is also a fake hardcoded value.',
    goal: 'On the confirmation screen, offer the customer a "Download confirmation" option that produces a formatted summary they can save, print, or share — with advisor details, date/time, location, reason, and a real unique reference number.',
    outcome: 'Customers leave the flow with something tangible. The confirmation is shareable. The reference number is unique and traceable.',
  },
];

const STATUS_COLORS = {
  queued:      { bg: 'rgba(96,125,139,0.12)', border: 'rgba(96,125,139,0.3)', text: '#546e7a', label: 'Queued' },
  'in-progress':{ bg: 'rgba(25,118,210,0.1)', border: 'rgba(25,118,210,0.35)', text: '#1565c0', label: 'In Progress' },
  done:        { bg: 'rgba(46,125,50,0.1)', border: 'rgba(46,125,50,0.35)', text: '#2e7d32', label: 'Done' },
};

function FeatureCard({ feature }) {
  const [open, setOpen] = useState(false);
  const s = STATUS_COLORS[feature.status] || STATUS_COLORS.queued;

  return (
    <div
      style={{
        border: `1px solid ${s.border}`,
        borderRadius: 12,
        marginBottom: 12,
        background: s.bg,
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          padding: '14px 16px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span style={{
          minWidth: 28,
          height: 28,
          borderRadius: '50%',
          background: s.text,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 13,
          flexShrink: 0,
        }}>
          {feature.number}
        </span>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 15, color: 'var(--text-primary, #1a1a1a)' }}>
          {feature.title}
        </span>
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.04em',
          color: s.text,
          textTransform: 'uppercase',
          background: '#fff',
          border: `1px solid ${s.border}`,
          borderRadius: 6,
          padding: '2px 8px',
          flexShrink: 0,
        }}>
          {s.label}
        </span>
        <span style={{ color: 'var(--text-secondary, #666)', fontSize: 18, flexShrink: 0 }}>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px 56px' }}>
          <Section label="Problem" color="var(--td-red)">{feature.problem}</Section>
          <Section label="Goal" color="#1565c0">{feature.goal}</Section>
          <Section label="Outcome" color="#2e7d32">{feature.outcome}</Section>
        </div>
      )}
    </div>
  );
}

function Section({ label, color, children }) {
  return (
    <div style={{ marginTop: 10 }}>
      <span style={{
        fontWeight: 700,
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color,
        display: 'block',
        marginBottom: 4,
      }}>
        {label}
      </span>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary, #333)', lineHeight: 1.55 }}>
        {children}
      </p>
    </div>
  );
}

const UpcomingFeaturesScreen = ({ onBack }) => {
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) return;
    setSubmitError('');
    const result = await submitTesterFeedback(feedback.trim());
    if (result.success) {
      setFeedback('');
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
    } else {
      setSubmitError(result.error || 'Submission failed — please try again.');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-primary, #f5f5f5)' }}>
      {/* Header */}
      <div style={{
        background: 'var(--td-green, #1a5e20)',
        color: '#fff',
        padding: '16px 16px 12px',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          {onBack && (
            <button onClick={onBack}
              style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, display: 'flex' }}>
              <MdArrowBack size={22} />
            </button>
          )}
          <MdRocketLaunch size={20} style={{ flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: 18 }}>Upcoming Features</span>
        </div>
        <p style={{ margin: 0, fontSize: 13, opacity: 0.85, lineHeight: 1.4 }}>
          These enhancements are queued for the Swipe advisor-booking flow. Tap a feature to read the details,
          then leave feedback at the bottom.
        </p>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 24px' }}>

        {/* Status legend */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_COLORS).map(([key, s]) => (
            <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: s.text }}>
              {key === 'done'
                ? <MdCheckCircle size={14} />
                : <MdRadioButtonUnchecked size={14} />}
              {s.label}
            </span>
          ))}
        </div>

        {/* Feature cards */}
        {FEATURES.map(f => <FeatureCard key={f.number} feature={f} />)}

        {/* Feedback section */}
        <div style={{
          marginTop: 24,
          background: 'var(--bg-card, #fff)',
          borderRadius: 12,
          border: '1px solid var(--border-color, #e0e0e0)',
          padding: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <MdFeedback size={20} color="var(--td-green, #1a5e20)" />
            <span style={{ fontWeight: 700, fontSize: 16 }}>Tester Feedback</span>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary, #666)', lineHeight: 1.5 }}>
            Have a question, spotted a bug, or want to flag something before build? Leave a note below.
            Submissions are saved to <code>docs/tester-feedback.md</code> with your username and timestamp.
          </p>
          <textarea
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            placeholder="e.g. Feature 3 — the self-serve routing should also handle 'overdraft' queries. Also, the advisor cards on mobile feel a bit tight..."
            rows={5}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              borderRadius: 8,
              border: '1px solid var(--border-color, #d0d0d0)',
              padding: '10px 12px',
              fontSize: 14,
              fontFamily: 'inherit',
              resize: 'vertical',
              background: 'var(--bg-primary, #fafafa)',
              color: 'var(--text-primary, #1a1a1a)',
            }}
          />
          <button
            onClick={handleSubmitFeedback}
            disabled={!feedback.trim() || submitted}
            style={{
              marginTop: 10,
              background: feedback.trim() && !submitted ? 'var(--td-green, #1a5e20)' : '#ccc',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 20px',
              fontWeight: 600,
              fontSize: 14,
              cursor: feedback.trim() && !submitted ? 'pointer' : 'not-allowed',
              transition: 'background 0.2s',
            }}
          >
            {submitted ? '✓ Feedback saved' : 'Send feedback'}
          </button>
          {submitError && (
            <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--td-red)' }}>{submitError}</p>
          )}
        </div>

        {/* Build info footer */}
        <div style={{ marginTop: 20, padding: '12px 0', borderTop: '1px solid var(--border-color, #e0e0e0)' }}>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary, #999)', lineHeight: 1.5 }}>
            Plans are processed automatically by the agent harness in <code>backlog/queued/</code>.<br />
            Completed plans move to <code>backlog/done/</code>. Failed plans move to <code>backlog/failed/</code>.<br />
            Brief prepared: 2026-08-14 · IVA Swipe Enhancement v1
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpcomingFeaturesScreen;
